import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import {
  setAvailablesEducationLevelList,
  setEducationLevelCollectives,
} from './educationLevelsSlice';

import {
  AuthorityChangePayload,
  Careers,
  EducationLevel,
  EducationLevelAuthority,
  EducationLevelContacts,
  EducationLevelContactsCreate,
  EducationLevelsResponse,
  PrincipalEducationLevel,
} from '../interfaces/educationLevels.interfaces';
import {
  EducationLevelCollective,
  EducationLevelCollectivePayload,
  EducationLevelCollectiveResponse,
} from '../interfaces/educationLevelsCollective.interfaces';
import { AccessState } from '../../../../types';

export const educationLevelsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: [
    'EducationLevels',
    'AvailablesOffers',
    'AvailablesOffersInfo',
    'EducationLevelsAvailables',
    'EducationLevelCollectives',
    'PrincipalEducationLevels',
    'AcademicOfferings',
    'EducationLevelContacts',
    'EducationLevelAuthority',
  ],
});
// Define a service using a base URL and expected endpoints
export const educationLevelsApi = educationLevelsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all EducationLevels
     */
    getEducationLevels: builder.query<
      EducationLevelsResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        type_sort?: string;
        type?: string;
        principalEducationLevelId?: number;
        statusId?: number;
        offerId?: number;
      }
    >({
      queryFn: async (
        {
          profileId,
          page,
          search,
          perPage,
          sort,
          type_sort = 'desc',
          type,
          principalEducationLevelId,
          statusId,
          offerId,
        },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<EducationLevelsResponse>('/education-levels', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort,
              search,
              sort,
              type,
              ...(principalEducationLevelId && { principal_id: principalEducationLevelId }),
              ...(offerId && { offer_id: offerId }),
              ...(statusId && { status_id: statusId }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'EducationLevels', id } as const)),
              { type: 'EducationLevels', id: 'LIST' },
            ]
          : [{ type: 'EducationLevels', id: 'LIST' }],
    }),

    /**
     * @GET all EducationLevels Avalibles
     */
    getAvailableCollective: builder.query<EducationLevel[], void>({
      queryFn: async (_, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EducationLevel[]>('/education-levels');

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET all EducationLevels Avalibles
     */
    getAvaliblesEducationLevels: builder.query<EducationLevel[], { profileId?: Number }>({
      queryFn: async ({ profileId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId || profileId;

          const { data } = await mainApi.get<EducationLevel[]>('/education-levels', {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          dispatch(setAvailablesEducationLevelList(data));

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' }],
    }),

    /**
     * @GET all careers availables
     */
    getAvailablesCareers: builder.query<EducationLevel[], { profileId?: Number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId || profileId;

          const { data } = await mainApi.get<EducationLevel[]>('/education-levels/career', {
            params: {
              user_profile_id,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' }],
    }),

    /**
     * @GET all EducationLevels Avalibles
     */
    getPrincipalEducationLevels: builder.query<PrincipalEducationLevel[], { profileId?: Number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<PrincipalEducationLevel[]>(
            '/education-levels/principal',
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'PrincipalEducationLevels', id: 'COMBO_PRINCIPAL_EL' }],
    }),

    /**
     * @GET all education levels Avalibles paged 0
     */
    getPagedAvaliblesEducationLevels: builder.query<EducationLevel[], { profileId: Number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get<EducationLevelsResponse>('/education-levels', {
            params: {
              user_profile_id: profileId,
              page: 1,
              size: 0,
              type_sort: 'desc',
            },
          });

          const allData = Array.isArray(data.data) ? data.data : [];

          return { data: allData };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' }],
    }),

    /**
     * @GET all careers
     */
    getAvailablesGroupedCareers: builder.query<Careers[], { profileId?: Number }>({
      queryFn: async ({ profileId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get<Careers[]>('/education-levels/group/career', {
            params: {
              user_profile_id,
              data: 'all',
              sort: 'principal_id',
              type_sort: 'desc',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' }],
    }),

    /**
     * @GET all education levels with vigent curriculum and optional offer id for filter carrer
     */
    getAvaliblesEducationLevelsVigentCurriculum: builder.query<
      EducationLevel[],
      { profileId?: Number; offerId?: number }
    >({
      queryFn: async ({ profileId, offerId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<EducationLevel[]>(
            '/education-levels/parents/mesh/vigents',
            {
              params: {
                user_profile_id,
                ...(offerId && { offer_id: offerId }),
                // page: 1,
                // size: 0,
                // type_sort: 'desc',
              },
            }
          );

          // const allData = data.data;

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' }],
    }),

    /**
     * @GET
     * Muestra niveles educativos Exclusivamente padre (que contenga principal_id = null)
     * con sus hijos con mallas vigentes.
     */
    getEducationLevelsParentsVM: builder.query<
      EducationLevel[],
      { profileId?: number; offerId?: number }
    >({
      queryFn: async ({ profileId, offerId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId || profileId;

          const { data } = await mainApi.get<EducationLevel[]>(
            '/education-levels/parents/mesh/vigents',
            {
              params: {
                user_profile_id,
                offer_id: offerId,
              },
            }
          );
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      //invalidatesTags: [{ type: 'SubjectNews', id: 'LIST' }],
    }),

    getEducationLevelsParentsAndChildren: builder.query<
      EducationLevel[],
      { profileId?: number; offerId?: number; filter?: boolean; postgraduate?: boolean }
    >({
      queryFn: async ({ profileId, offerId, filter, postgraduate }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;
          const { data } = await mainApi.get<EducationLevel[]>('/education-levels/parents', {
            params: {
              user_profile_id,
              offer_id: offerId,
              ...(postgraduate && { post_grade: postgraduate }),
              ...(filter && { filter }),
            },
          });
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [
        {
          type: 'EducationLevelsAvailables',
          id: 'COMBO_EDLVLS_LIST',
        },
      ],
    }),

    /**
     * @GET educationLevel by id
     */
    getEducationLevel: builder.query<
      EducationLevel,
      { profileId?: number; educationLevelId: number }
    >({
      queryFn: async ({ profileId, educationLevelId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;
          const { data } = await mainApi.get<EducationLevel>(
            `/education-levels/${educationLevelId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'EducationLevels', profileId }],
    }),

    /**
     * @POST create educationLevel
     */
    addEducationLevel: builder.mutation<
      EducationLevel,
      { profileId: number; educationLevelPayload: Partial<EducationLevel> }
    >({
      queryFn: async ({ profileId, educationLevelPayload }) => {
        try {
          const { data } = await mainApi.post<EducationLevel>('/education-levels', {
            user_profile_id: profileId,
            ...educationLevelPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'EducationLevels', id: 'LIST' },
        { type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' },
        { type: 'EducationLevels', id: 'COMBO_OFFERS' },
        { type: 'AvailablesOffersInfo', id: 'COMBO_OFFERS_INFO' },
        { type: 'PrincipalEducationLevels', id: 'COMBO_PRINCIPAL_EL' },
        { type: 'AcademicOfferings', id: 'INFO_OFFER' },
      ],
    }),
    /**
     * @PUT update educationLevel
     */
    updateEducationLevel: builder.mutation<
      EducationLevel,
      {
        profileId: number;
        educationLevelId: number;
        educationLevelPayload: Partial<EducationLevel>;
      }
    >({
      queryFn: async ({ profileId, educationLevelId, educationLevelPayload }) => {
        try {
          const { data } = await mainApi.put<EducationLevel>(
            `/education-levels/${educationLevelId}`,
            {
              user_profile_id: profileId,
              ...educationLevelPayload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { profileId }) => [
        { type: 'EducationLevels', id: 'LIST' },
        { type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' },
        { type: 'EducationLevels', id: 'COMBO_OFFERS' },
        { type: 'AvailablesOffersInfo', id: 'COMBO_OFFERS_INFO' },
        { type: 'PrincipalEducationLevels', id: 'COMBO_PRINCIPAL_EL' },
        { type: 'AcademicOfferings', profileId },
      ],
    }),

    /**
     * @DELETE remove educationLevel
     */
    deleteEducationLevel: builder.mutation<
      EducationLevel,
      { profileId: number; educationLevelId: number }
    >({
      queryFn: async ({ profileId, educationLevelId }) => {
        try {
          const { data } = await mainApi.delete(`education-levels/${educationLevelId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'EducationLevels', id: 'LIST' },
        { type: 'PrincipalEducationLevels', id: 'COMBO_PRINCIPAL_EL' },
        { type: 'EducationLevelsAvailables', id: 'COMBO_EDLVLS_LIST' },
      ],
    }),

    // Collectives asign to education level

    /**
     * @POST Add collective education level
     */
    addCollectiveEducationLevel: builder.mutation<
      EducationLevelCollective,
      { jointAcademic: EducationLevelCollective }
    >({
      queryFn: async ({ jointAcademic }) => {
        try {
          const { data } = await mainApi.post<EducationLevelCollective>('/joint-academics', {
            ...jointAcademic,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET GET collective by education level
     */
    getCollectiveEducationLevel: builder.query<
      EducationLevelCollectiveResponse[],
      { educationLevelId: number }
    >({
      queryFn: async ({ educationLevelId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EducationLevelCollectiveResponse[]>(
            `/joint-academics/education-level/${educationLevelId}`
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'EducationLevelCollectives', id } as const)),
              { type: 'EducationLevelCollectives', id: 'LIST_COLLECTIVES' },
            ]
          : [{ type: 'EducationLevelCollectives', id: 'LIST_COLLECTIVES' }],
    }),

    /**
     * @GET GET all collectives
     */
    getAllCollectiveEducationLevel: builder.query<
      EducationLevelCollectiveResponse[],
      { educationLevelId: number }
    >({
      queryFn: async ({ educationLevelId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EducationLevelCollectiveResponse[]>(
            `/education-level/${educationLevelId}/joint-academics`
          );

          dispatch(setEducationLevelCollectives(data));
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @PUT Update collective
     */
    updateEducationLevelCollective: builder.mutation<
      EducationLevelCollectivePayload,
      { collective: number; payload: EducationLevelCollectivePayload }
    >({
      queryFn: async ({ collective, payload }, { dispatch }) => {
        try {
          const { data } = await mainApi.put<EducationLevelCollectivePayload>(
            `/joint-academics/${collective}`,
            { ...payload }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EducationLevelCollectives', id: 'LIST_COLLECTIVES' }],
    }),

    /**
     * @DELETE Delete collective
     */
    deleteEducationLevelCollective: builder.mutation<
      EducationLevelCollectivePayload,
      { collectiveId: number }
    >({
      queryFn: async ({ collectiveId }, { dispatch }) => {
        try {
          const { data } = await mainApi.delete(`/joint-academics/${collectiveId}`);

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EducationLevelCollectives', id: 'LIST_COLLECTIVES' }],
    }),

    /**
     * @GET Delete collective education level
     */
    deleteCollectiveCollaborator: builder.query<
      EducationLevelCollectiveResponse[],
      { educationLevelId: number }
    >({
      queryFn: async ({ educationLevelId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EducationLevelCollectiveResponse[]>(
            `/education-level/${educationLevelId}/joint-academic`
          );

          dispatch(setEducationLevelCollectives(data));
          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET GET all collectives
     */
    getContactByEducationLevel: builder.query<
      EducationLevelContacts,
      { profileId?: number; educationLevelId: number }
    >({
      queryFn: async ({ profileId, educationLevelId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<EducationLevelContacts>(
            `/getContactByEducationLevel/${educationLevelId}`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'EducationLevelContacts', id: 'LIST' }],
    }),

    /**
     * @GET GET all collectives
     */
    getAuthorityEducationLevel: builder.query<
      EducationLevelAuthority,
      { profileId?: number; educationLevelId: number }
    >({
      queryFn: async ({ profileId, educationLevelId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<EducationLevelAuthority>(
            `/education-levels/${educationLevelId}/collaborator`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) => [{ type: 'EducationLevelAuthority', id: 'PERSON' }],
    }),

    /**
     * @POST Create contacts
     */
    createEduLevelContacts: builder.mutation<
      any,
      { payload: EducationLevelContactsCreate; noInvalidate?: boolean }
    >({
      queryFn: async ({ payload, noInvalidate = false }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.post<any>(`/education-levels-contacts`, {
            user_profile_id,
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { noInvalidate }) =>
        noInvalidate ? [] : [{ type: 'EducationLevelContacts', id: 'LIST' }],
    }),

    /**
     * @PUT Update contacts
     */
    updateEduLevelContacts: builder.mutation<
      any,
      { payload: EducationLevelContacts; noInvalidate?: boolean }
    >({
      queryFn: async ({ payload, noInvalidate = false }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          //no hay put
          const { data } = await mainApi.post<any>(`/education-levels-contacts`, {
            user_profile_id,
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { noInvalidate }) =>
        noInvalidate ? [] : [{ type: 'EducationLevelContacts', id: 'LIST' }],
    }),
    /**
     * @PUT Update authority
     */
    updateEducationLevelAuthority: builder.mutation<
      any,
      { payload: AuthorityChangePayload; noInvalidate?: boolean }
    >({
      queryFn: async ({ payload, noInvalidate = false }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };
          const user_profile_id = currentProfile?.profileId;

          //no hay put
          const { data } = await mainApi.put<any>(`/education-levels/set-collaborator`, {
            user_profile_id,
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { noInvalidate }) =>
        noInvalidate ? [] : [{ type: 'EducationLevelAuthority', id: 'PERSON' }],
    }),

    /**
     * @DELETE Delete Network
     */
    deleteEduLevelNetwork: builder.mutation<
      EducationLevelCollectivePayload,
      { newtworkId: number }
    >({
      queryFn: async ({ newtworkId }, { dispatch }) => {
        try {
          const { data } = await mainApi.delete(`education-levels-network/${newtworkId}`);

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EducationLevelContacts', id: 'LIST_COLLECTIVES' }],
    }),

    /**
     * @DELETE Delete Contact
     */
    deleteEduLevelContact: builder.mutation<EducationLevelCollectivePayload, { contactId: number }>(
      {
        queryFn: async ({ contactId }, { dispatch }) => {
          try {
            const { data } = await mainApi.delete(`/education-levels-contacts/${contactId}`);

            return { data };
          } catch (error: any) {
            return { error };
          }
        },
        invalidatesTags: [{ type: 'EducationLevelContacts', id: 'LIST_COLLECTIVES' }],
      }
    ),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetEducationLevelsQuery,
  useGetAvaliblesEducationLevelsQuery,
  useAddEducationLevelMutation,
  useDeleteEducationLevelMutation,
  useGetEducationLevelQuery,
  useUpdateEducationLevelMutation,
  useGetPagedAvaliblesEducationLevelsQuery,
  useGetPrincipalEducationLevelsQuery,
  useGetAvailablesCareersQuery,
  useGetAllCollectiveEducationLevelQuery,
  useAddCollectiveEducationLevelMutation,
  useGetEducationLevelsParentsVMQuery,
  useDeleteEducationLevelCollectiveMutation,
  useUpdateEducationLevelCollectiveMutation,
  useDeleteCollectiveCollaboratorQuery,
  useGetCollectiveEducationLevelQuery,
  useGetAvailableCollectiveQuery,
  useGetEducationLevelsParentsAndChildrenQuery,
  useLazyGetEducationLevelsParentsAndChildrenQuery,

  useGetAvaliblesEducationLevelsVigentCurriculumQuery,
  useGetAvailablesGroupedCareersQuery,

  useLazyGetEducationLevelQuery,

  useGetContactByEducationLevelQuery,
  useCreateEduLevelContactsMutation,
  useUpdateEduLevelContactsMutation,
  useDeleteEduLevelContactMutation,
  useDeleteEduLevelNetworkMutation,
  useGetAuthorityEducationLevelQuery,
  useUpdateEducationLevelAuthorityMutation,
} = educationLevelsApi;
